import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import MaterialTable from "@material-table/core";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import {
  TablePagination,
  IconButton, Tooltip, Button, Icon, useTheme,
} from "@material-ui/core";
import {
  SubdirectoryArrowRight,
} from "@material-ui/icons";
import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import {ComboSelect, LightTooltip} from '../../../controls';
import useColour, {getStatusColour} from '../../../services/settings/useColour';
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import SendCateringOrderDialog from "./SendCateringOrderDialog";
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import { useCateringOrders } from "../../../services/flights";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";
import EditCateringDialog from "../../Trips/components/TripListDialogs/EditCateringDialog";
import {fetch as fetchDefaultServiceTypes} from "../../../services/settings/useAirportServiceTypes";
import {fetch as fetchTripLegServices} from "../../../services/trips/useTripLegServices";
import CompanyUuidLookup from '../../Companies/components/CompanyUuidLookup';


const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}


const FlightsMissingCateringOrdersList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const [state] = useState({
    canEdit: props.canEdit,
  });

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required)
    extraKeys.push(true);

  const orders = useCateringOrders({tableRef, queryClient, extraKeys});

  const colours = useColour({code: "CATERING TEXT"});
  const backgroundColours = useColour({code: "CATERING BACKGROUND"});

  const refreshList = async () => {
    queryClient.invalidateQueries(["CateringOrders", 0, 0, true]);
  }

  const handleNewOrder = async (record) => {

    let supplierUuid = null;
    let orderTemplateUuid = null;
    const defaultServiceTypes = await fetchDefaultServiceTypes({queryClient, extraKeys: [record.originAirportCode], useCache: false});
    if (defaultServiceTypes && Array.isArray(defaultServiceTypes)) {
      const cateringService = __.find(defaultServiceTypes, {serviceCode: "CATERING"});
      if (cateringService && __.has(cateringService, "companyUuid"))
        supplierUuid = cateringService.companyUuid;
      if (cateringService && __.has(cateringService, "orderTemplate"))
        orderTemplateUuid = cateringService.orderTemplate;
    }

    try {
      const response = await axios.get(`${apiSitePrefix}/triplegs/getTripLeg/${record.tripLegUuid}`);
      if (!response.data) {
        SystemData.alert("Error", `Could not retrieve flight information. Server is not responding`);
      } else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const tripLeg = response.data[0];
        if (!tripLeg.crew || (Array.isArray(tripLeg.crew) && tripLeg.crew.length < 1)){
          SystemData.openAlert("Catering Prerequisites", "Crew numbers must be captured in order to generate catering orders");
        } else {
          const tripLegServices = await fetchTripLegServices({queryClient, extraKeys: [tripLeg.tripLegUuid], useCache: false});
          const cateringService = __.find(tripLegServices, {serviceCode: "CATERING"});
          if (cateringService && cateringService.serviceProviderUuid) {
            supplierUuid = cateringService.serviceProviderUuid;
          }
          if (supplierUuid)
            tripLeg["supplierUuid"] = supplierUuid;
          if (orderTemplateUuid)
            tripLeg["orderTemplateUuid"] = orderTemplateUuid;
          eventBus.trigger("EditCateringDialog.open", tripLeg);
        }
      } else {
        SystemData.alert("Error", `Could not retrieve flight information. Server could not find the flight`);
      }
    } catch (error) {
      SystemData.alert("Error", `Could not retrieve flight information. Server is not responding`);
    }
  }

  const getColumns = (props) => {

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...(ColumnWidth(110, [10,10], undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "originAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.originAirportCode} />}>
            <div>
              {rowData.status === "SECTOR" ? <div><SubdirectoryArrowRight/><span> {rowData.originAirportCode} </span></div> : <span> {rowData.originAirportCode} </span>}
            </div>
          </LightTooltip>
        ),
      },
      // {
      //   ...baseColumn,
      //   ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
      //   title: "Status",
      //   field: "catering",
      //   editable: "never",
      //   fullWidth: true,
      //   sorting: true,
      //   cellStyle: (value, rowData) => {
      //     const backgroundColor = getStatusColour(backgroundColours, value);
      //     const color = getStatusColour(colours, value);
      //     const {cellStyle} = ColumnWidth(100);
      //     return ({...cellStyle, color, backgroundColor, textAlign: "center", border: `1px solid #d0d0d0`, fontWeight: 600});
      //   },
      //   render: rowData => (
      //     <ComboSelect
      //       group="TRIP LEG INDICATORS"
      //       subGroup=""
      //       value={rowData.catering}
      //       displayOnly={true}
      //     />
      //   ),
      // },
      {
        ...baseColumn,
        ...ColumnWidth(120,[10,10],undefined,undefined,undefined,undefined,false),
        title: "Flight Number",
        field: "flightNo",
        render: (props) => (
          <div style={{ width: 162 }}>
            {props.airlineCode}&nbsp;{props.flightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, undefined, undefined)),
        title: "Flight Date",
        field: "scheduledDepartureTimeUTC",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.scheduledDepartureTimeUTC).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.scheduledDepartureTimeUTC ? moment.utc(rowData.scheduledDepartureTimeUTC).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(110, undefined, undefined, undefined, undefined)),
        title: "Departure Time",
        field: "scheduledDepartureTimeUTC",
        render: rowData => (
          <div>{rowData.scheduledDepartureTimeUTC ? moment.utc(rowData.scheduledDepartureTimeUTC).format("HH:mm") : ""}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(110, undefined, undefined, undefined, undefined)),
        title: "Crew Count",
        field: "crewCount",
        editable: "never",
      },
      {
        ...baseColumn,
        title: "Supplier",
        field: "serviceProviderUuid",
        render: rowData=> (
          <CompanyUuidLookup
            value={rowData.serviceProviderUuid}
            type="render"
            hideCode={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        //icons={{ DetailPanel: React.forwardRef((props, ref) => <Icon>unfold_more</Icon> )}}
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Action: _props => {
            let props = {..._props};
            if (typeof props.action.action === "function") {
              props.action = {..._props.action, ..._props.action.action(_props.data)};
            }
            if (props.action.icon==="generate") {
              return (
                <Tooltip title={props.action.tooltip}>
                  <Button
                    style={{height: 24}}
                    variant="contained"
                    color="primary"
                    startIcon={<PlayIcon/>}
                    className={classes.button}
                    onClick={() => {
                      props.action.onClick(props.data)
                    }}
                  >
                    {props.action.text}
                  </Button>
                </Tooltip>
              )
            } else if (props.action.icon) {
              if (props.action.tooltip) {
                return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
              }
              return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
            } else {
              return (<></>)
            }
          }
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          (rowData) => ({
            icon: "generate",
            text: "Generate",
            tooltip: "Create catering order for the flight",
            onClick: handleNewOrder,
            disabled: !state.canEdit
          }),
        ]}
        title="Flights Missing Catering Orders"
        columns={getColumns({canEdit: state.canEdit})}
        data={orders.data}
        editable={{
          isEditable: () => { return false; },
          isDeletable: () => { return false; },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />

      <EditCateringDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleUpdateFlight={()=> {}}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
      />

    </div>
  );

}

export default FlightsMissingCateringOrdersList;
